import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import { InvoiceStanding } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceStanding";
import { asLocalDate } from "@freightsimple/time/branded-types";
import type { Column } from "@freightsimple/ui-core/Table/Table";
import { DateCell } from "@freightsimple/ui-core/Cells/DateCell";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { InvoicesStatusPill } from "@/Shared/Invoices/CustomComponents/InvoicesStatusPill";
import { invoiceTypeDescription } from "@/Shared/Invoices/Helpers/invoiceTypeDescription";
import {
  formatIdentifierDateLine,
  getShipmentDisplayDetails,
} from "@/Shared/Invoices/Helpers/invoiceUtils";
import ShipmentSmallFromTo from "@/Shared/Shipment/CustomComponents/ShipmentSmallFromTo";

export interface PaymentsInvoiceTableRow {
  invoice: Invoice;
  shipment: Shipment;
}

/**
 * Column builders shared by the Make Payments selection table and the
 * receipt's paid-invoices table. Both show the same freight — the receipt
 * just doesn't need the payment-planning columns (status, due date).
 *
 * No refund "-" prefix on the amount, unlike InvoicesTable: refunds can't
 * reach these tables (`shouldBePaid` only passes issued charges).
 */
export const paymentsInvoiceColumns = {
  charge(): Column<PaymentsInvoiceTableRow> {
    return {
      key: "charge",
      header: "Charge",
      render: ({ invoice }) => (
        <Stack gap={0} align="start">
          <T size="base" weight="medium" color="secondary">
            {invoiceTypeDescription(invoice.invoiceType)}
          </T>
          <T size="xs" weight="light" color="muted">
            {formatIdentifierDateLine(invoice)}
          </T>
        </Stack>
      ),
      sortable: (a, b) =>
        // ISO strings, so lexicographic compare is chronological.
        a.invoice.invoiceDate.localeCompare(b.invoice.invoiceDate),
    };
  },

  status(): Column<PaymentsInvoiceTableRow> {
    return {
      key: "status",
      header: "Status",
      hug: true,
      render: ({ invoice }) => <InvoicesStatusPill invoice={invoice} />,
    };
  },

  route(): Column<PaymentsInvoiceTableRow> {
    return {
      key: "route",
      header: "Route",
      render: ({ shipment }) => {
        const details = getShipmentDisplayDetails(shipment);
        return (
          <ShipmentSmallFromTo
            pickupBusinessName={details.pickupBusinessName}
            pickupCity={details.pickupCity}
            pickupReference={shipment.pickupReferenceNumber}
            deliveryBusinessName={details.deliveryBusinessName}
            deliveryCity={details.deliveryCity}
            deliveryReference={shipment.deliveryReferenceNumber}
            compact={true}
          />
        );
      },
    };
  },

  due(): Column<PaymentsInvoiceTableRow> {
    return {
      key: "due",
      header: "Due",
      hug: true,
      render: ({ invoice }) => (
        <DateCell
          date={asLocalDate(invoice.dueDate)}
          // Server-computed, so this dialog can't disagree with the Invoices
          // screen about what "overdue" means. Disputed invoices stay neutral.
          color={
            invoice.standing === InvoiceStanding.Overdue ? "red" : "default"
          }
        />
      ),
      sortable: (a, b) => a.invoice.dueDate.localeCompare(b.invoice.dueDate),
    };
  },

  amount(): Column<PaymentsInvoiceTableRow> {
    return {
      key: "amount",
      header: "Amount",
      align: "right",
      render: ({ invoice }) => (
        <Row gap={1} justify="end">
          {/* Crisp `default`: the amount is the headline value of the row.
              The currency beside it is a unit suffix, so it stays `muted`. */}
          <T size="lg" weight="medium" nowrap>
            ${invoice.amount.toFixed(2)}
          </T>
          <T size="lg" weight="medium" color="muted" nowrap>
            {invoice.currency === Currency.Cad ? "CAD" : "USD"}
          </T>
        </Row>
      ),
      sortable: (a, b) => a.invoice.amount - b.invoice.amount,
    };
  },
};
